import React, { useState, useEffect } from 'react';

import { FormError } from '../../../../components/form';
import { Button, Tooltip } from '../../../../components/general';
import { SideIcon } from '../../../../components/icons';
import { TemplateShotBlock } from '../../../../components/video';

import { isPlural } from '../../../../utils';

interface Props {
  wildcards: number[];
  onChangeWildcards: (newWildcards: number[]) => void;
  error?: string;
}

const ProjectWildcardsSelector: React.FC<Props> = (props) => {
  const { wildcards, onChangeWildcards, error } = props;

  const [editIndex, setEditIndex] = useState(-1);
  const [editText, setEditText] = useState('');

  // clear the edit index when clicked outside of wildcard
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editIndex !== -1) {
        const element = document.getElementById(`${editIndex}-wildcard`);
        if (!element?.contains(e.target as Node)) setEditIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editIndex]);

  const handleAdd = () => {
    const wildcardsTemp = [...wildcards, 5];
    onChangeWildcards(wildcardsTemp);
  };

  const handleDelete = (index: number) => {
    const wildcardsTemp = [...wildcards];
    wildcardsTemp.splice(index, 1);
    onChangeWildcards(wildcardsTemp);

    setEditIndex(-1);
  };

  const handleEdit = (index: number) => {
    if (editIndex === -1) {
      setEditIndex(index);
      setEditText(wildcards[index].toString());
    } else {
      setEditIndex(-1);
      setEditText('');
    }
  };

  const onChangeEditText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const wildcardsTemp = [...wildcards];
    wildcardsTemp[editIndex] = parseInt(e.target.value) ?? 0;
    onChangeWildcards(wildcardsTemp);

    setEditText(e.target.value);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <TemplateShotBlock type="selects" />

        {wildcards.map((wildcard, index) => (
          <React.Fragment key={index}>
            <TemplateShotBlock type="wildcard" id={`${index}-wildcard`}>
              <div className="absolute w-full h-full">
                <SideIcon type="edit" onClick={() => handleEdit(index)} />

                {wildcards.length > 1 && (
                  <SideIcon type="delete" onClick={() => handleDelete(index)} />
                )}
              </div>

              <div className="text-sm font-bold">Wildcard</div>

              {index === editIndex ? (
                <input
                  className="w-[70%] h-[1.5em] p-0 text-center text-sm border-x-0 border-t-0 border-b-1 border-grey-200 border-solid focus:border-grey-200 z-[3]"
                  type="number"
                  name="editText"
                  value={editText}
                  onChange={onChangeEditText}
                  placeholder="0"
                  required
                />
              ) : (
                <div className="text-sm">
                  {wildcard} second{isPlural(wildcard)}
                </div>
              )}
            </TemplateShotBlock>

            <TemplateShotBlock
              type="selects"
              className={index === wildcards.length - 1 ? 'mr-8' : ''}
            />
          </React.Fragment>
        ))}

        <Tooltip
          text="We don't recommend using more than 3 wildcards"
          hide={wildcards.length < 3}
        >
          <Button
            onClick={handleAdd}
            variant="custom"
            colour="grey-outline-salmon"
            className="w-[6rem] sm:w-[7rem] aspect-video flex flex-col items-center justify-center text-2xl rounded-md"
          >
            +
          </Button>
        </Tooltip>
      </div>

      <FormError errMsg={error} />
    </div>
  );
};

export default ProjectWildcardsSelector;
